<template>
  <div>
    <div class="row mb-3">
      <div class="col-12">
        <h4>Créer un nouveau paramètre</h4>
      </div>
    </div>

    <div class="row align-items-center">
      <div class="col-md-4 mb-3">
        <label>Clé <span class="text-danger">*</span></label>
        <input 
          type="text" 
          v-model="setting.key" 
          class="form-control" 
          :class="{ 'is-invalid': errors.key }"
          :disabled="loading"
          placeholder="Ex: stock, theme, etc."
          @blur="validateKey"
        />
        <div v-if="errors.key" class="invalid-feedback">
          {{ errors.key }}
        </div>
        <small class="text-muted">La clé doit être unique et ne peut contenir que des lettres, chiffres et underscores</small>
      </div>
      <div class="col-md-4 mb-3">
        <label>Type de valeur <span class="text-danger">*</span></label>
        <select 
          v-model="setting.valueType" 
          class="form-select"
          :disabled="loading"
          @change="handleValueTypeChange"
        >
          <option value="string">String</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="array">Array</option>
          <option value="object">Object</option>
        </select>
        <small class="text-muted">Sélectionnez le type de données pour cette valeur</small>
      </div>
      <div class="col-md-4 mb-3">
        <label>Valeur</label>
        <!-- String or Number input -->
        <input 
          v-if="setting.valueType === 'string' || setting.valueType === 'number'"
          :type="setting.valueType === 'number' ? 'number' : 'text'" 
          v-model="setting.value"
          class="form-control" 
          :class="{ 'is-invalid': errors.value }"
          :disabled="loading"
          :placeholder="setting.valueType === 'number' ? 'Ex: 100' : 'Entrez la valeur'"
          @blur="validateValue"
        />
        <!-- Boolean select -->
        <select 
          v-else-if="setting.valueType === 'boolean'"
          v-model="setting.value"
          class="form-select"
          :disabled="loading"
        >
          <option :value="true">True</option>
          <option :value="false">False</option>
        </select>
        <!-- Array or Object textarea -->
        <textarea 
          v-else
          v-model="setting.value"
          class="form-control" 
          :class="{ 'is-invalid': errors.value }"
          :disabled="loading"
          rows="4"
          placeholder='Pour array: ["item1", "item2"]&#10;Pour object: {"key": "value"}'
          @blur="validateValue"
        ></textarea>
        <div v-if="errors.value" class="invalid-feedback d-block">
          {{ errors.value }}
        </div>
        <small v-if="setting.valueType === 'array' || setting.valueType === 'object'" class="text-muted">
          Format JSON valide requis. Exemple array: ["item1", "item2"] | Exemple object: {"key": "value"}
        </small>
      </div>
    </div>

    <div class="row mt-3 justify-content-between">
      <div class="col-auto">
        <button 
          class="btn btn-secondary" 
          @click="$router.push({ name: 'settings-index' })"
          :disabled="loading"
        >
          <i class="bi bi-arrow-left"></i> Retour
        </button>
      </div>
      <div class="col-auto">
        <button 
          class="btn btn-success" 
          @click="save(setting)"
          :disabled="loading || !setting.key"
        >
          <i v-if="loading" class="spinner-border spinner-border-sm me-2"></i>
          <i v-else class="bi bi-check-square-fill"></i> 
          {{ loading ? 'Enregistrement...' : 'Enregistrer' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      errors: {},
      setting: {
        key: '',
        valueType: 'string',
        value: ''
      }
    };
  },
  methods: {
    validateKey() {
      this.errors = {};
      if (!this.setting.key || this.setting.key.trim() === '') {
        this.errors.key = 'La clé est obligatoire.';
        return false;
      }
      // Check if key contains only alphanumeric characters and underscores
      if (!/^[a-zA-Z0-9_]+$/.test(this.setting.key)) {
        this.errors.key = 'La clé ne peut contenir que des lettres, chiffres et underscores.';
        return false;
      }
      return true;
    },
    validateValue() {
      this.errors = {};
      if (this.setting.valueType === 'array' || this.setting.valueType === 'object') {
        if (!this.setting.value || this.setting.value.trim() === '') {
          this.errors.value = 'La valeur est requise pour ce type.';
          return false;
        }
        try {
          JSON.parse(this.setting.value);
        } catch (e) {
          this.errors.value = 'Format JSON invalide. Veuillez vérifier la syntaxe.';
          return false;
        }
      } else if (this.setting.valueType === 'number') {
        if (this.setting.value === '' || isNaN(this.setting.value)) {
          this.errors.value = 'Veuillez entrer un nombre valide.';
          return false;
        }
      }
      return true;
    },
    handleValueTypeChange() {
      // Reset value when type changes
      if (this.setting.valueType === 'boolean') {
        this.setting.value = true;
      } else if (this.setting.valueType === 'array') {
        this.setting.value = '[]';
      } else if (this.setting.valueType === 'object') {
        this.setting.value = '{}';
      } else {
        this.setting.value = '';
      }
    },
    async save(data) {
      // Clear previous errors
      this.errors = {};
      
      // Validate key
      if (!this.validateKey()) {
        return;
      }
      
      // Validate value
      if (!this.validateValue()) {
        return;
      }

      try {
        this.loading = true;
        
        // Process value based on type
        let processedValue = data.value;
        if (data.valueType === 'array' || data.valueType === 'object') {
          try {
            // Validate and parse JSON
            const parsed = JSON.parse(data.value);
            processedValue = JSON.stringify(parsed);
          } catch (e) {
            this.errors.value = 'Format JSON invalide. Veuillez vérifier la syntaxe.';
            this.loading = false;
            return;
          }
        } else if (data.valueType === 'boolean') {
          processedValue = data.value === true || data.value === 'true' || data.value === 1 || data.value === '1';
        } else if (data.valueType === 'number') {
          processedValue = parseFloat(data.value) || 0;
        } else if (data.valueType === 'string') {
          processedValue = String(data.value || '');
        }

        const createData = {
          key: data.key.trim(),
          valueType: data.valueType,
          value: processedValue
        };

        await this.$store.dispatch("setting/store", createData);
      } catch (error) {
        console.error("Error creating setting:", error);
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },
    handleError(error) {
      let errorMessage = "Erreur lors de la création du paramètre.";
      const response = error.response;
      
      if (response && response.data) {
        if (response.data.message) {
          errorMessage = response.data.message;
        } else if (response.data.errors && response.data.errors.key && response.data.errors.key[0]) {
          errorMessage = response.data.errors.key[0];
        }
        
        // Set field errors if available
        if (response.data.errors) {
          this.errors = response.data.errors;
        }
      }
      
      this.$store.dispatch("notification/store", {
        status: "error",
        message: errorMessage,
      }, { root: true });
    }
  }
};
</script>